import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

const schema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  companyName: z.string().optional(),
  profession: z.enum(["financial_planner", "estate_lawyer", "trust_company", "accountant", "other"]),
  phoneNumber: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAdvisorDialog({ open, onOpenChange }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ tempPassword: string; discountCode: string; email: string } | null>(null);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
      profession: "financial_planner",
      phoneNumber: "",
    },
  });

  // D-170.6: converted from a raw apiRequest. Class A — creates an advisor account with a
  // generated temp password and discount code. `users.email` is UNIQUE so a duplicate account
  // is impossible; the double-submit failure is the second attempt erroring over the first.
  const createFlight = useSingleFlight();
  const createAdvisorMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await apiRequest("POST", "/api/admin/advisors", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create advisor");
      }
      return await res.json();
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const resp = await createFlight.run(() => createAdvisorMutation.mutateAsync(data));
      if (!resp) return; // duplicate fire
      queryClient.invalidateQueries({ queryKey: ["/api/admin/advisors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setResult({ tempPassword: resp.tempPassword, discountCode: resp.discountCode, email: data.email });
      form.reset();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Advisor Created</DialogTitle>
              <DialogDescription>
                Share these credentials with the advisor. They should change their password on first login.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="font-mono text-sm">{result.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Temporary Password</p>
                <p className="font-mono text-sm font-bold">{result.tempPassword}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Discount Code</p>
                <p className="font-mono text-sm font-bold text-primary">{result.discountCode}</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create Advisor Account</DialogTitle>
              <DialogDescription>
                Create an advisor portal account. A temporary password and discount code will be generated.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField control={form.control} name="fullName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl><Input placeholder="Jane Smith" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" placeholder="jane@firm.com" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="companyName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name <span className="text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl><Input placeholder="Smith Financial Planning" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="profession" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profession</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="financial_planner">Financial Planner</SelectItem>
                        <SelectItem value="estate_lawyer">Estate Lawyer</SelectItem>
                        <SelectItem value="trust_company">Trust Company</SelectItem>
                        <SelectItem value="accountant">Accountant</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phoneNumber" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone <span className="text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl><Input type="tel" placeholder="+1234567890" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                  <Button type="submit" disabled={isLoading || createFlight.inFlight}>
                    {isLoading ? "Creating..." : "Create Advisor"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
